import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { ApplicationState } from '~/Store';

import { useAvailabilityCreateFolder } from './adapters/useAvailabilityCreateFolder';
import { useAvailabilityData } from './adapters/useAvailabilityData';
import { CreateFolderButton } from './components/CreateFolderButton';
import { CreateFolderDialog } from './components/CreateFolderDialog';
import { ExportButton } from './components/ExportButton';
import { Filters, FiltersValue } from './components/Filters';
import { Header } from './components/Header';
import { IntroSection } from './components/IntroSection';
import { RowExpansionControls } from './components/RowExpansionControls';
import {
	AVAILABILITY_CONTENT_PADDING,
	AVAILABILITY_CONTENT_WIDTH,
	DDD_SHARE_THRESHOLD,
	EXPORT_LIMIT,
} from './constants';
import { useAvailabilityExportSelection } from './hooks/useAvailabilityExportSelection';
import { useAvailabilityFilters } from './hooks/useAvailabilityFilters';
import { AvailabilityListComponentProps } from './types/AvailabilityListProps';
import { buildAvailabilityParams } from './utils/availabilityQueryParams';
import { collectPageExpandKeys } from './utils/availabilityRowExpandKey';

const AVAILABILITY_BREADCRUMBS = [{ name: 'Dostupnosti' }];

export interface AvailabilityPageBaseProps {
	ListComponent: React.ComponentType<AvailabilityListComponentProps>;
}

export function AvailabilityPageBase({ ListComponent }: AvailabilityPageBaseProps) {
	const canCreateFolder = useSelector(
		(state: ApplicationState) =>
			state.accessToken.accessToken?.subscription?.systemModules.has('product-groups') ?? false
	);

	const { filters, page, perPage, changeFilters, changePage, changePerPage, resolveLabels } = useAvailabilityFilters();

	const { data, isFetching, isError } = useAvailabilityData(buildAvailabilityParams(filters, page, perPage));
	const createFolder = useAvailabilityCreateFolder(filters, data?.items);

	const total = data?.pagination.total ?? 0;
	const selection = useAvailabilityExportSelection({ filters, total });

	const filtersRef = useRef<HTMLDivElement>(null);
	const tableSectionRef = useRef<HTMLDivElement>(null);
	const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set());

	const expansionResetKey = `${filters.items.map((item) => item.id).join(',')}|${filters.rs.join(',')}`;

	useEffect(() => {
		setExpandedKeys(new Set());
	}, [expansionResetKey]);

	const handleToggleExpand = useCallback((key: string) => {
		setExpandedKeys((prev) => {
			const next = new Set(prev);
			if (next.has(key)) {
				next.delete(key);
			} else {
				next.add(key);
			}
			return next;
		});
	}, []);

	const handlePageChange = useCallback(
		(nextPage: number) => {
			setExpandedKeys(new Set());
			changePage(nextPage);
		},
		[changePage]
	);

	const handlePerPageChange = useCallback(
		(nextPerPage: number) => {
			setExpandedKeys(new Set());
			changePerPage(nextPerPage);
		},
		[changePerPage]
	);

	useEffect(() => {
		const filtersEl = filtersRef.current;
		const section = tableSectionRef.current;

		if (!filtersEl || !section || typeof ResizeObserver === 'undefined') {
			return undefined;
		}

		const observer = new ResizeObserver(() => {
			section.style.setProperty('--availability-filters-h', `${filtersEl.offsetHeight}px`);
		});
		observer.observe(filtersEl);

		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		if (data) {
			resolveLabels(data.items);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	const handleFiltersChange = (value: FiltersValue) => {
		changeFilters(value);
	};

	const hasResults = !isError && !!data && data.items.length > 0;
	const totalExpandable = hasResults ? collectPageExpandKeys(data.items).size : 0;

	const handleToggleExpandAll = useCallback(() => {
		if (expandedKeys.size >= totalExpandable && totalExpandable > 0) {
			setExpandedKeys(new Set());
		} else if (data?.items) {
			setExpandedKeys(collectPageExpandKeys(data.items));
		}
	}, [expandedKeys.size, totalExpandable, data?.items]);

	const isExportDisabled =
		!hasResults ||
		isFetching ||
		selection.exceedsLimitWithoutFilter ||
		(selection.showSelection && selection.selectedIds.size === 0);

	return (
		<div className='w-full bg-white'>
			<Header breadcrumbs={AVAILABILITY_BREADCRUMBS} title='Monitor dostupnosti' />

			<IntroSection />

			<div ref={tableSectionRef} data-sticky-toolbar className='w-full bg-gray-100 mb-6'>
				<div ref={filtersRef} className='static xl:sticky xl:top-0 xl:z-20 w-full bg-gray-100 border-b border-gray-200'>
					<div className={`${AVAILABILITY_CONTENT_PADDING} py-4 ${AVAILABILITY_CONTENT_WIDTH} mx-auto space-y-4`}>
						<Filters
							value={filters}
							onChange={handleFiltersChange}
							middleSlot={
								hasResults ? (
									<RowExpansionControls
										expandedCount={expandedKeys.size}
										totalExpandable={totalExpandable}
										disabled={isFetching}
										onToggle={handleToggleExpandAll}
									/>
								) : null
							}
							rightSlot={
								<>
									{canCreateFolder ? (
										<CreateFolderButton
											disabled={!createFolder.hasActiveFilter || createFolder.isResolvingFolderName}
											isCreating={createFolder.isCreating || createFolder.isResolvingFolderName}
											onClick={createFolder.openCreateFolderDialog}
										/>
									) : null}
									<ExportButton
										isExporting={selection.isExporting}
										disabled={isExportDisabled}
										onClick={selection.handleExport}
									/>
								</>
							}
						/>

						{selection.showSelection && hasResults ? (
							<p className='text-sm text-gray-500 -mt-4'>
								Exportovat lze maximálně {EXPORT_LIMIT} záznamů. Vybráno: {selection.selectedIds.size}/{EXPORT_LIMIT}.
							</p>
						) : null}
					</div>
				</div>

				<div className={`${AVAILABILITY_CONTENT_PADDING} pt-4 pb-4 ${AVAILABILITY_CONTENT_WIDTH} mx-auto`}>
					<ListComponent
						data={data}
						isFetching={isFetching}
						isError={isError}
						dddShareThreshold={DDD_SHARE_THRESHOLD}
						selectable={selection.showSelection}
						selectedIds={selection.selectedIds}
						selectionDisabled={selection.selectedIds.size >= EXPORT_LIMIT}
						onToggleSelect={(group, checked) => selection.toggleSelect(group.id, checked)}
						perPage={perPage}
						onPageChange={handlePageChange}
						onPerPageChange={handlePerPageChange}
						expandedKeys={expandedKeys}
						onToggleExpand={handleToggleExpand}
					/>
				</div>
			</div>

			{createFolder.isDialogOpen ? (
				<CreateFolderDialog
					initialName={createFolder.folderName}
					isCreating={createFolder.isCreating}
					onClose={createFolder.closeCreateFolderDialog}
					onSubmit={createFolder.handleCreateFolder}
				/>
			) : null}
		</div>
	);
}
